/**
 * People held more than once. Only the advisor can say whether two records are the same person, so
 * this asks instead of merging on their behalf, and keeping both is a real answer, not a way of
 * postponing one. A choice that could not be saved says so.
 */
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@design-system';
import type { useCrmDuplicates } from '@client/hooks/useCrmDuplicates';
import { crmName as crmDisplayName } from '@/util/crmNames';
import { fieldMapCopy } from '../lib/field-map-copy';
import { FieldMapAlert } from './field-map-alert';

interface DuplicatePeopleProps {
  duplicates: ReturnType<typeof useCrmDuplicates>;
}

export function DuplicatePeople({ duplicates }: DuplicatePeopleProps) {
  const copy = fieldMapCopy.duplicates;

  if (duplicates.isLoading || duplicates.needsAttention.length === 0) return null;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{copy.title(duplicates.needsAttention.length)}</CardTitle>
        <CardDescription>{copy.intro}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!!duplicates.resolveError && (
          <FieldMapAlert
            title={fieldMapCopy.alerts.duplicatesTitle}
            error={duplicates.resolveError}
          />
        )}
        {duplicates.needsAttention.map(group => (
          <div
            key={group.matchKey}
            className="rounded-lg border border-gray-200 p-3"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="font-medium text-gray-900">{group.matchKey}</span>
              {group.needsReview && group.status !== 'unresolved' && (
                <Badge variant="secondary">{copy.changed}</Badge>
              )}
            </div>

            <div className="mb-3 space-y-1">
              {group.members.map(member => (
                <div
                  key={member.id}
                  className="flex items-center justify-between text-sm text-gray-700"
                >
                  <span>
                    {[member.firstName, member.lastName].filter(Boolean).join(' ') || copy.unnamed}
                    <span className="text-gray-500"> · {crmDisplayName(member.provider)}</span>
                  </span>
                  <Button
                    size="sm"
                    variant={group.keptProspectId === member.id ? 'default' : 'outline'}
                    onClick={() => duplicates.keepOne(group.matchKey, member.id)}
                    disabled={duplicates.isResolving}
                  >
                    {group.keptProspectId === member.id ? copy.kept : copy.keep}
                  </Button>
                </div>
              ))}
            </div>

            <Button
              size="sm"
              variant="ghost"
              onClick={() => duplicates.keepBoth(group.matchKey)}
              disabled={duplicates.isResolving}
            >
              {copy.keepBoth}
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
